/**
 * FRAMED help detection -- "this help route is inside the help flyover".
 *
 * The problem this exists to solve
 * -------------------------------
 * The help flyover (`HelpFlyover.vue`) does not re-fetch and re-render help
 * content itself; it embeds the REAL help route in a same-origin iframe.
 * That is deliberate, and it is what Jake's requirement demands: "the
 * routes can stay the same and be loaded in an iframe in the flyover,
 * which would preserve the ability to link to them directly." One
 * implementation, one URL space, and a direct link / bookmark / new tab
 * keeps working because the standalone page never went away.
 *
 * WHAT THIS FLAG DOES, AND NO LONGER DOES
 * ---------------------------------------
 * It originally chose the APP LAYOUT: help routes are mounted inside
 * DistributorLayout / StorefrontLayout, and `?framed=1` was what resolved
 * them to PublicLayout so the flyover would not nest a second sidebar
 * inside its own panel.
 *
 * That job has moved. App.vue now keys the layout on `isHelpRoute` -- the
 * SUBTREE, not the flag -- because the flag-conditional version had the
 * polarity backwards: framed (where the parent window already supplies
 * chrome) rendered bare, while standalone (which is supposed to "read like
 * a static document") inherited the full app shell. Both presentations are
 * minimal chrome now, so the layout no longer depends on this flag.
 *
 * What REMAINS this flag's job is the IN-ARTICLE navigation. `HelpRenderer`
 * reads it to decide whether to draw the sidebar list of sibling articles
 * and the breadcrumb: yes in the flyover, which is a browsing surface; no
 * standalone, which is a document. That is a real distinction the layout
 * rule cannot express, so the flag is not redundant -- it is narrower.
 *
 * WHY A QUERY FLAG AND NOT `window.self !== window.top`
 * ----------------------------------------------------
 * Frame-busting detection is invisible in the URL, so the framed rendering
 * could not be reproduced, linked, or tested without building an iframe
 * first. The flag is explicit, greppable, and survives in-iframe
 * navigation between articles (see `installHelpFramedQueryPersistence`),
 * which the flyover depends on -- the reader clicks "Route an order" in
 * the in-article nav and must keep its navigation.
 *
 * SECURITY NOTE. This flag chooses PRESENTATION. It grants nothing: it
 * does not touch the auth guard, the `help.access` capability check, or
 * the `requireToken` allowlist. `?framed=1` on a route the caller cannot
 * reach is still a redirect to login. The same holds for `isHelpRoute`
 * below, which decides layout: removing visual chrome from a page is not
 * the same as removing the auth boundary in front of it, and neither
 * function is consulted by anything that authorizes. That separation is
 * asserted in `router/__tests__/help-framed.spec.js` and, against the real
 * guard, in `router/__tests__/help-routes-auth-boundary.spec.js` -- and it
 * is why these are safe to derive from a URL at all.
 */

export const HELP_FRAMED_PARAM = "framed";

/**
 * The help subtrees. ONE definition, two consumers.
 *
 * Originally this list only bounded the blast radius of `?framed=1`. It
 * now also answers "is this a help route at all", which App.vue's
 * `currentLayout` asks in order to render help with minimal chrome in
 * BOTH presentations (see `isHelpRoute`). Keeping a single list means the
 * flag's scope and the layout rule can never disagree about what counts
 * as help.
 */
const HELP_PREFIXES = [
    "/distributor/help/",
    "/help/shopper",
    // SuperAdmin help. Load-bearing in BOTH of this list's jobs, and the
    // damage from omitting it is invisible to a route test: these routes
    // carry `meta.layout = 'superadmin'`, and App.vue consults `isHelpRoute`
    // BEFORE `meta.layout` -- so without this entry the flyover would nest
    // the entire SA console inside its own panel, and `?framed=1` would mean
    // nothing, costing the in-article sidebar and breadcrumb.
    "/admin/help/superadmin",
];

/** True when `path` is inside one of the help subtrees. */
function isHelpPath(path) {
    return HELP_PREFIXES.some(
        (prefix) => path === prefix || path.startsWith(prefix)
    );
}

/**
 * True when `route` is a help route, in EITHER presentation.
 *
 * This is the layout question, and it is deliberately independent of
 * `?framed=1`. Help is documentation on both surfaces: inside the flyover
 * the parent window already supplies the app chrome, and standalone the
 * page is meant to "read like a static document" (Jake). Either way the
 * answer is the same -- no app shell -- so the layout rule keys on the
 * SUBTREE, not on a query param a URL can carry.
 *
 * Note this is intentionally a weaker test than `isHelpFramed`: it asks
 * only where the route is, not what the query says. It must never be used
 * to decide anything but presentation. Auth for these paths lives in
 * `requireToken`'s allowlist and the routes' own `requireCapability`
 * guards, and is untouched by this function.
 *
 * @param {object|null} route  Vue Router's `$route` (or `{ path }`)
 * @returns {boolean}
 */
export function isHelpRoute(route) {
    if (!route) return false;
    return isHelpPath(route.path || "");
}

/**
 * True when `route` is a help route rendering INSIDE the flyover iframe,
 * i.e. the navigational presentation.
 *
 * BOTH halves are required, and the path half stays load-bearing even now
 * that the flag no longer picks the layout: it keeps `?framed=1` from
 * meaning anything on routes that are not help. Narrowing a flag's domain
 * to its purpose is worth keeping regardless of what it currently drives,
 * and it is what lets this be safely read from a URL at all.
 *
 * @param {object|null} route  Vue Router's `$route` (or a plain
 *                             `{ path, query }`)
 * @returns {boolean}
 */
export function isHelpFramed(route) {
    if (!route) return false;

    if (!isHelpPath(route.path || "")) return false;

    const value = route.query?.[HELP_FRAMED_PARAM];
    if (value === undefined || value === null) return false;

    // Present-but-falsey values explicitly opt OUT, matching
    // isStorefrontPreview. Keeps `?framed=0` from being a confusing
    // half-state, and means a stray flag can always be turned off in the
    // address bar rather than only by deleting it.
    const normalized = String(value).toLowerCase();
    if (["0", "false", "no"].includes(normalized)) return false;

    return true;
}

/**
 * Add the framed flag to a help path, for use as an iframe `src`.
 *
 * Takes the plain path the contextual resolver already produced
 * (`resolveHelpTarget`) so there is exactly ONE place that knows how a
 * help URL is built, and this only decorates it. The undecorated path is
 * what the open-in-new-tab control uses -- a new tab is the standalone
 * presentation, a static document with no in-article nav, so it must NOT
 * carry this flag.
 *
 * A contextual target may carry a `#heading` fragment (`resolveHelpTarget`
 * appends one for a section documented partway down an article). The flag
 * has to go BEFORE that fragment: in a URL the fragment runs to the end, so
 * appending here would produce `...#heading?framed=1` -- a fragment of
 * literally "heading?framed=1", which matches no anchor and leaves the query
 * unparsed, silently un-framing the flyover AND breaking the scroll.
 *
 * @param {string} path  e.g. "/distributor/help/owner/build-a-store"
 * @returns {string}     e.g. "/distributor/help/owner/build-a-store?framed=1"
 */
export function withHelpFramedFlag(path) {
    if (!path) return path;

    const hashAt = path.indexOf("#");
    const base = hashAt === -1 ? path : path.slice(0, hashAt);
    const fragment = hashAt === -1 ? "" : path.slice(hashAt);

    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${HELP_FRAMED_PARAM}=1${fragment}`;
}

/**
 * Keep `?framed=1` attached as the reader navigates INSIDE the iframe.
 *
 * HelpRenderer's nav and breadcrumb are `<router-link>`s to bare paths
 * (`/distributor/help/owner/route-an-order`). Inside the iframe the first
 * such click would drop the query, and the article would abruptly LOSE
 * its sidebar and breadcrumb mid-flyover -- the reader would be one click
 * into a browsing surface that had just stopped letting them browse.
 * (Before the layout rule moved off this flag it was the app chrome that
 * appeared instead; either way, dropping the flag mid-flyover is wrong.)
 * This guard re-attaches it on any help-to-help navigation that started
 * framed, the same job `installPreviewQueryPersistence` does for the
 * storefront preview.
 *
 * Only ever ADDS the flag to a help route when the departure route was a
 * framed help route -- it cannot introduce framing anywhere else, and
 * navigating OUT of help (or to an explicit `framed=0`) drops it.
 *
 * @param {import('vue-router').Router} router
 */
export function installHelpFramedQueryPersistence(router) {
    router.beforeEach((to, from, next) => {
        if (!isHelpFramed(from)) return next();
        if (isHelpFramed(to)) return next();

        if (!isHelpPath(to.path || "")) return next();

        // An explicit opt-out must be honoured, not re-added.
        const raw = to.query?.[HELP_FRAMED_PARAM];
        if (raw !== undefined && raw !== null) return next();

        return next({
            ...to,
            query: { ...to.query, [HELP_FRAMED_PARAM]: "1" },
            replace: true,
        });
    });
}
